import React, { useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import Text from "@/components/ui/text";
import { useTranslation } from "react-i18next";
import { simpleDate } from "@/utils/datetime";
import { AnnualQuotaWidget } from "@/entities/reporting/types";
import { Confetti } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import { TFunction } from "i18next";

interface DonutDataType {
  name: string;
  value: number;
  color?: string;
  pattern?: "solid" | "striped" | "gradient";
  label?: string;
  zIndex?: number;
  startAngle: number;
  endAngle: number;
}

const START_ANGLE = 90;
const FULL_CIRCLE = 360;

interface Props {
  data?: AnnualQuotaWidget;
}

const getSeriesDefaults = (
  key: string,
  t: TFunction<"translation", undefined>
) => {
  switch (key) {
    case "quota":
      return {
        label: t("salesVSQuota.quota"),
        color: "var(--Charts-Grey-bar, #3A4860)",
        pattern: "solid" as const,
        zIndex: 1
      };
    case "actualSales":
      return {
        label: t("salesVSQuota.actualSales"),
        color: "#54F5BA",
        pattern: "gradient" as const,
        zIndex: 3
      };
    case "committedDeals":
      return {
        label: t("salesVSQuota.committedDeals"),
        color: "#10A47D",
        pattern: "striped" as const,
        zIndex: 2
      };
    default:
      return {
        label: key,
        color: "#667085",
        pattern: "solid" as const,
        zIndex: 0
      };
  }
};

const AnnualSalesVSQuota: React.FC<Props> = ({ data }) => {
  const { t } = useTranslation();
  const chartData = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }

    const annualData = data[0];
    const { quota, actualSales, committedDeals } = annualData.series;

    const quotaValue = quota.value;
    const actualSalesPercentage =
      quotaValue > 0 ? (actualSales.value / quotaValue) * 100 : 0;
    const committedDealsPercentage =
      quotaValue > 0 ? (committedDeals.value / quotaValue) * 100 : 0;

    const actualSweep =
      (Math.min(actualSalesPercentage, 100) / 100) * FULL_CIRCLE;
    const committedStartAngle = START_ANGLE - actualSweep;
    const committedSweep = Math.min(
      (committedDealsPercentage / 100) * FULL_CIRCLE,
      FULL_CIRCLE - actualSweep
    );

    const rawData: DonutDataType[] = [
      {
        name: "quota",
        value: 100, // Full circle
        startAngle: START_ANGLE,
        endAngle: START_ANGLE - FULL_CIRCLE
      },
      {
        name: "actualSales",
        value: actualSalesPercentage,
        startAngle: START_ANGLE,
        endAngle: START_ANGLE - actualSweep
      },
      {
        name: "committedDeals",
        value: committedDealsPercentage,
        startAngle: committedStartAngle,
        endAngle: committedStartAngle - committedSweep
      }
    ];

    return rawData
      .map((item) => ({
        ...item,
        ...getSeriesDefaults(item.name, t)
      }))
      .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
  }, [data, t]);

  const actualSalesPercentage = useMemo(() => {
    if (!data || data.length === 0) {
      return 0;
    }

    const annualData = data[0];
    const { quota, actualSales } = annualData.series;

    return quota.value > 0
      ? Math.round((actualSales.value / quota.value) * 100)
      : 0;
  }, [data]);

  const getChartTitle = () => {
    if (!data || data.length === 0) {
      return "";
    }

    const annualData = data[0];

    if (
      !annualData.startMonth ||
      !annualData.startYear ||
      !annualData.endMonth ||
      !annualData.endYear
    ) {
      return "";
    }

    // If start month is January (1), return the year
    if (annualData.startMonth === 1) {
      return annualData.startYear.toString();
    }

    const yearRange =
      annualData.startYear === annualData.endYear
        ? annualData.startYear.toString()
        : `${annualData.startYear} - ${annualData.endYear}`;

    const startDate = new Date(annualData.startYear, annualData.startMonth - 1);
    const endDate = new Date(annualData.endYear, annualData.endMonth - 1);

    const dateRange = `${simpleDate(startDate, "short", false, true, true)} - ${simpleDate(endDate, "short", false, true, true)}`;

    return `${t("salesVSQuota.fiscalYear", { year: yearRange })} (${dateRange})`;
  };

  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center">
      <div className="relative w-full flex-1 flex items-center justify-center">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <defs>
              {/* Striped pattern for committed deals */}
              <pattern
                id="pattern-striped-committedDeals"
                patternUnits="userSpaceOnUse"
                width="8"
                height="8"
                patternTransform="rotate(-45)"
              >
                <rect width="8" height="8" fill="transparent" />
                <line
                  x1="0"
                  y1="4"
                  x2="8"
                  y2="4"
                  stroke="#10A47D"
                  strokeWidth="2"
                />
              </pattern>
              {/* Gradient for actual sales */}
              <linearGradient
                id="gradient-actualSales"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" stopColor="#54F5BA" />
                <stop offset="100%" stopColor="#14C993" />
              </linearGradient>
            </defs>
            {chartData.map((entry: DonutDataType, index: number) => {
              const getFill = () => {
                switch (entry.pattern) {
                  case "striped":
                    return "url(#pattern-striped-committedDeals)";
                  case "gradient":
                    return "url(#gradient-actualSales)";
                  default:
                    return entry.color;
                }
              };

              const getStroke = () => {
                return entry.pattern === "striped" ? "#00F5A3" : "none";
              };

              const getStrokeWidth = () => {
                return entry.pattern === "striped" ? 2 : 0;
              };

              return (
                <Pie
                  key={`pie-${index}`}
                  data={[{ value: 100 }]} // Use full value for proper rendering
                  cx="50%"
                  cy="50%"
                  innerRadius={83}
                  outerRadius={95}
                  startAngle={entry.startAngle}
                  endAngle={entry.endAngle}
                  dataKey="value"
                  cornerRadius={
                    entry.name === "actualSales" ||
                    entry.name === "committedDeals"
                      ? 7.5
                      : 0
                  }
                >
                  <Cell
                    fill={getFill()}
                    stroke={getStroke()}
                    strokeWidth={getStrokeWidth()}
                  />
                </Pie>
              );
            })}
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex items-center justify-center">
          {actualSalesPercentage >= 100 ? (
            <div className="flex flex-col items-center justify-center text-center">
              <div className="flex items-center justify-center mb-1">
                <IconContainer size="5" color="secondary">
                  <Confetti />
                </IconContainer>
              </div>
              <Text variant="bodySecondary" className="text-center">
                {t("salesVSQuota.quotaAchieved")}
              </Text>
              <Text variant="title1H1">{actualSalesPercentage}%</Text>
            </div>
          ) : (
            <Text variant="title1H1">{actualSalesPercentage}%</Text>
          )}
        </div>
      </div>
      <div className="mt-4 text-center">
        <Text variant="smBodySecondary">{getChartTitle()}</Text>
      </div>
    </div>
  );
};

export default AnnualSalesVSQuota;
